import { appPriceLabel, appPublicName, appTitleKind } from '@/content/apps';
import { legalRoutes } from '@/content/company';
import { homeFaq, pricingFaq } from '@/content/faq';
import { cases } from '@/content/cases';
import { guides } from '@/content/guides';
import { cheapestPaidPlanPriceCents, formatPlanPrice } from '@/content/plans';
import { sectors } from '@/content/sectors';
import { appPath, buildApps, buildPlans } from '@/seo/catalog';
import {
  appLd,
  articleLd,
  breadcrumbLd,
  faqLd,
  graph,
  organizationLd,
  productLd,
  webPageLd,
  websiteLd,
  type JsonLd,
} from '@/seo/jsonld';

/**
 * Registro único de SEO por rota.
 *
 * Tudo que um buscador ou assistente de IA lê sobre uma página sai daqui:
 * título, descrição, canonical, JSON-LD, entrada no sitemap e no `llms.txt`.
 * O navegador usa o mesmo registro para atualizar a aba ao navegar, então o
 * título que o Google vê e o que o visitante vê são sempre o mesmo.
 *
 * Página nova precisa entrar aqui, senão sai do build sem HTML próprio e fica
 * de fora do sitemap. O `scripts/prerender.mjs` falha o build se uma rota fixa
 * do `App.tsx` não tiver registro aqui.
 */
export interface RouteSeo {
  path: string;
  title: string;
  description: string;
  /** Página de transação ou legal que não deve disputar busca. */
  noindex?: boolean;
  jsonLd: JsonLd;
  sitemap?: { priority: number; changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly' };
  /** Grupo para o `llms.txt`. */
  llmsSection?: 'Produto' | 'Apps' | 'Setores' | 'Guias' | 'Casos de clientes' | 'Empresa';
  /** Uma linha para o `llms.txt`. */
  llmsSummary?: string;
}

const MARCA = 'Operax';

/** Título com a marca no fim. Passou de 62 caracteres, a marca sai para o título não ser cortado no Google. */
function titulo(texto: string): string {
  const comMarca = `${texto} | ${MARCA}`;
  return comMarca.length <= 62 ? comMarca : texto;
}

function precoDeEntrada(): string {
  const cents = cheapestPaidPlanPriceCents(buildPlans) ?? 12990;
  return formatPlanPrice(cents);
}

function staticRoutes(): RouteSeo[] {
  const aPartirDe = precoDeEntrada();
  const home: RouteSeo = {
    path: '/',
    title: titulo('Plataforma de atendimento no WhatsApp com CRM'),
    description: `Atendimento e vendas no WhatsApp oficial da Meta: inbox, funil, agenda, Pix e NFS-e no mesmo login. A partir de ${aPartirDe}/mês.`,
    jsonLd: graph([
      organizationLd(),
      websiteLd(),
      productLd(buildPlans),
      webPageLd('/', 'Operax: plataforma de atendimento no WhatsApp com CRM', 'Página inicial da Operax'),
      faqLd(homeFaq, '/'),
    ]),
    sitemap: { priority: 1, changefreq: 'weekly' },
    llmsSection: 'Produto',
    llmsSummary: 'Visão geral da plataforma e por onde começar.',
  };

  const produto: RouteSeo = {
    path: '/produto',
    title: titulo('CRM para WhatsApp com API oficial da Meta'),
    description:
      'Inbox no WhatsApp oficial com filas, distribuição, histórico e funil de vendas colado em cada conversa. Veja como a Operax organiza o atendimento da equipe.',
    jsonLd: graph([
      productLd(buildPlans),
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Produto', path: '/produto' },
      ]),
      webPageLd('/produto', 'CRM para WhatsApp com API oficial da Meta', 'Como funciona o atendimento na Operax'),
    ]),
    sitemap: { priority: 0.9, changefreq: 'monthly' },
    llmsSection: 'Produto',
    llmsSummary: 'Inbox, filas, funil e conexão oficial com o WhatsApp.',
  };

  const apps: RouteSeo = {
    path: '/apps',
    title: titulo('Apps para WhatsApp: agenda, estoque, Pix, NFS-e e mais'),
    description: `${buildApps.length} apps para ligar na Operax quando a operação pedir: agenda, estoque, PDV, Pix, nota fiscal, contratos e IA, sem trocar de sistema.`,
    jsonLd: graph([
      {
        '@type': 'ItemList',
        name: 'Apps da Operax',
        itemListElement: buildApps.map((app, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: app.name,
          url: `https://operax.digital${appPath(app.slug)}`,
        })),
      },
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Apps', path: '/apps' },
      ]),
    ]),
    sitemap: { priority: 0.9, changefreq: 'weekly' },
    llmsSection: 'Apps',
    llmsSummary: 'Catálogo completo de apps, com o que vem incluso e o que é add-on.',
  };

  const setores: RouteSeo = {
    path: '/setores',
    title: titulo('CRM para WhatsApp por setor: clínica, oficina, varejo e mais'),
    description:
      'Como a Operax atende clínicas, oficinas, restaurantes, varejo, serviços, beleza e eventos no WhatsApp oficial, com os apps de cada segmento.',
    jsonLd: graph([
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Setores', path: '/setores' },
      ]),
    ]),
    sitemap: { priority: 0.8, changefreq: 'monthly' },
    llmsSection: 'Setores',
    llmsSummary: 'Visão dos setores atendidos.',
  };

  const precos: RouteSeo = {
    path: '/precos',
    title: titulo(`Preços: CRM para WhatsApp a partir de ${aPartirDe}/mês`),
    description: `Planos da Operax a partir de ${aPartirDe}/mês, sem fidelidade. A taxa da Meta vai direto na conta da sua empresa. Veja usuários e apps de cada plano.`,
    jsonLd: graph([
      productLd(buildPlans),
      faqLd(pricingFaq, '/precos'),
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Preços', path: '/precos' },
      ]),
    ]),
    sitemap: { priority: 0.9, changefreq: 'weekly' },
    llmsSection: 'Produto',
    llmsSummary: 'Planos, preços, o que inclui e perguntas frequentes.',
  };

  const calculadora: RouteSeo = {
    path: '/calculadora',
    title: titulo('Calculadora de preço do plano'),
    description:
      'Informe quantas pessoas atendem e quantos números de WhatsApp você usa, escolha os apps e veja o plano da Operax que cabe na sua operação e o valor mensal.',
    jsonLd: graph([
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Calculadora', path: '/calculadora' },
      ]),
    ]),
    sitemap: { priority: 0.6, changefreq: 'monthly' },
    llmsSection: 'Produto',
    llmsSummary: 'Simulação do plano pelo tamanho da equipe e apps escolhidos.',
  };

  const sobre: RouteSeo = {
    path: '/sobre',
    title: titulo('Sobre a Operax: empresa, segurança e LGPD'),
    description:
      'Quem opera a Operax, CNPJ, sede em Timóteo/MG, conexão oficial com a Meta como Provedor de Tecnologia verificado, isolamento de dados por empresa e LGPD.',
    jsonLd: graph([
      organizationLd(),
      { '@type': 'AboutPage', '@id': 'https://operax.digital/sobre#pagina', url: 'https://operax.digital/sobre', about: { '@id': 'https://operax.digital/#organizacao' } },
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Sobre', path: '/sobre' },
      ]),
    ]),
    sitemap: { priority: 0.6, changefreq: 'monthly' },
    llmsSection: 'Empresa',
    llmsSummary: 'Empresa, dados cadastrais, segurança e LGPD.',
  };

  const clientes: RouteSeo = {
    path: '/clientes',
    title: titulo('Clientes: resultados reais com WhatsApp oficial'),
    description:
      'Quem opera com a Operax: 94% de entrega na cobrança da Protvaço, agenda automática em consultório, jornada de aluno e atendimento de supermercado no WhatsApp.',
    jsonLd: graph([
      {
        '@type': 'CollectionPage',
        '@id': 'https://operax.digital/clientes#pagina',
        name: 'Clientes da Operax',
        url: 'https://operax.digital/clientes',
      },
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Clientes', path: '/clientes' },
      ]),
    ]),
    sitemap: { priority: 0.7, changefreq: 'monthly' },
    llmsSection: 'Casos de clientes',
    llmsSummary: 'Empresas que operam com a Operax e os resultados que autorizaram publicar.',
  };

  const contato: RouteSeo = {
    path: legalRoutes.contact,
    title: titulo('Contato: fale com vendas e suporte'),
    description:
      'Fale com a Operax por WhatsApp, chat ou e-mail. Atendimento de segunda a sexta, das 9h às 18h (horário de Brasília).',
    jsonLd: graph([
      { '@type': 'ContactPage', '@id': 'https://operax.digital/contato#pagina', url: 'https://operax.digital/contato', about: { '@id': 'https://operax.digital/#organizacao' } },
      organizationLd(),
    ]),
    sitemap: { priority: 0.5, changefreq: 'yearly' },
    llmsSection: 'Empresa',
    llmsSummary: 'Canais de contato e horário de atendimento.',
  };

  const guiasIndex: RouteSeo = {
    path: '/guias',
    title: titulo('Guias sobre WhatsApp oficial para empresas'),
    description:
      'Guias práticos sobre a API oficial do WhatsApp: quanto custa, diferença para o app Business, como conectar o número e como escolher um CRM.',
    jsonLd: graph([
      {
        '@type': 'CollectionPage',
        '@id': 'https://operax.digital/guias#pagina',
        name: 'Guias da Operax',
        hasPart: guides.map((g) => ({ '@type': 'Article', headline: g.title, url: `https://operax.digital/guias/${g.slug}` })),
      },
      breadcrumbLd([
        { name: 'Início', path: '/' },
        { name: 'Guias', path: '/guias' },
      ]),
    ]),
    sitemap: { priority: 0.7, changefreq: 'weekly' },
    llmsSection: 'Guias',
    llmsSummary: 'Índice dos guias.',
  };

  const legal = (path: string, nome: string, descricao: string): RouteSeo => ({
    path,
    title: titulo(nome),
    description: descricao,
    jsonLd: graph([webPageLd(path, nome, descricao)]),
    sitemap: { priority: 0.2, changefreq: 'yearly' },
  });

  return [
    home,
    produto,
    apps,
    setores,
    precos,
    calculadora,
    sobre,
    clientes,
    contato,
    guiasIndex,
    legal(
      legalRoutes.privacy,
      'Política de Privacidade',
      'Como a Operax coleta, usa, guarda e protege dados pessoais, em conformidade com a LGPD.',
    ),
    legal(
      legalRoutes.terms,
      'Termos de Uso',
      'Regras de uso da plataforma Operax: contratação, pagamento, cancelamento, responsabilidades e foro.',
    ),
    legal(
      legalRoutes.dataDeletion,
      'Exclusão de Dados',
      'Como pedir a exclusão dos seus dados na Operax e o que acontece com eles depois do pedido.',
    ),
  ];
}

/** Buscadores cortam a descrição em ~160 caracteres; corta na última palavra inteira. */
export function metaDescription(texto: string, limite = 158): string {
  const limpo = texto.replace(/\s+/g, ' ').trim();
  if (limpo.length <= limite) return limpo;
  const corte = limpo.slice(0, limite - 1);
  return `${corte.slice(0, corte.lastIndexOf(' ')).replace(/[\s,;:.]+$/, '')}…`;
}

function appRoutes(): RouteSeo[] {
  return buildApps.map((app) => {
    const path = appPath(app.slug);
    const preco = appPriceLabel(app);
    const soon = app.tier === 'soon' || app.availability === 'coming_soon';
    // Preço como veio ("R$ 99,90/mês"); "Incluso no plano" vira minúscula no meio da frase.
    const sufixo = soon ? 'em breve' : preco ? (/^(a partir de )?R\$/.test(preco) ? preco : preco.toLowerCase()) : '';
    return {
      path,
      title: titulo(`${appPublicName(app)}: ${appTitleKind(app)}${sufixo ? `, ${sufixo}` : ''}`),
      description: metaDescription(app.description ?? app.name),
      jsonLd: graph([
        appLd(app, path),
        breadcrumbLd([
          { name: 'Início', path: '/' },
          { name: 'Apps', path: '/apps' },
          { name: appPublicName(app), path },
        ]),
      ]),
      sitemap: { priority: soon ? 0.4 : 0.7, changefreq: 'weekly' },
      llmsSection: 'Apps',
      llmsSummary: `${app.description ?? ''}${soon ? ' (em breve)' : preco ? ` Preço: ${preco}.` : ''}`.trim(),
    };
  });
}

function sectorRoutes(): RouteSeo[] {
  return sectors.map((sector) => {
    const path = `/setores/${sector.slug}`;
    return {
      path,
      title: titulo(sector.searchTerm),
      description: sector.lead,
      jsonLd: graph([
        webPageLd(path, sector.headline, sector.lead),
        sector.faq.length > 0 ? faqLd(sector.faq, path) : undefined,
        breadcrumbLd([
          { name: 'Início', path: '/' },
          { name: 'Setores', path: '/setores' },
          { name: sector.name, path },
        ]),
      ]),
      sitemap: { priority: 0.8, changefreq: 'monthly' },
      llmsSection: 'Setores',
      llmsSummary: sector.lead,
    };
  });
}

function guideRoutes(): RouteSeo[] {
  return guides.map((guide) => {
    const path = `/guias/${guide.slug}`;
    return {
      path,
      title: titulo(guide.title),
      description: guide.description,
      jsonLd: graph([
        articleLd({
          path,
          headline: guide.title,
          description: guide.description,
          published: guide.published,
          updated: guide.updated,
        }),
        guide.faq.length > 0 ? faqLd(guide.faq, path) : undefined,
        breadcrumbLd([
          { name: 'Início', path: '/' },
          { name: 'Guias', path: '/guias' },
          { name: guide.title, path },
        ]),
      ]),
      sitemap: { priority: 0.8, changefreq: 'monthly' },
      llmsSection: 'Guias',
      llmsSummary: guide.summary,
    };
  });
}

function caseRoutes(): RouteSeo[] {
  return cases.map((item) => {
    const path = `/casos/${item.slug}`;
    return {
      path,
      title: titulo(item.title),
      description: item.description,
      jsonLd: graph([
        articleLd({
          path,
          headline: item.title,
          description: item.description,
          published: item.published,
          updated: item.updated,
        }),
        breadcrumbLd([
          { name: 'Início', path: '/' },
          { name: item.company, path },
        ]),
      ]),
      sitemap: { priority: 0.7, changefreq: 'monthly' },
      llmsSection: 'Casos de clientes',
      llmsSummary: item.summary,
    };
  });
}

/** Rotas que existem só para transação: renderizam no navegador e ficam fora da busca. */
export const transactionalPaths = ['/assinar', '/solicitar-orcamento'] as const;
export const transactionalPrefixes = ['/orcamento/'] as const;

let cache: RouteSeo[] | null = null;

export function allRoutes(): RouteSeo[] {
  if (!cache) cache = [...staticRoutes(), ...appRoutes(), ...sectorRoutes(), ...guideRoutes(), ...caseRoutes()];
  return cache;
}

export function seoForPath(pathname: string): RouteSeo | undefined {
  const normalizado = pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
  return allRoutes().find((route) => route.path === normalizado);
}
